import React from 'react'
import {
  Button,
  Form,
  FormInstance,
  Input,
  Modal,
  Select,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import { productApi } from 'api/product'
import {
  EProductStatus,
  ICharacteristic,
  IFrontVariation,
  IFrontVariationProduct,
  ISubCategoryCharacteristic
} from 'models/product'
import { productTags } from 'shared/constants/product'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { showServerError } from 'shared/utils/showServerError'
import { capitalizeFirstLetter } from '../utils/characteristicsValidation'
import { toVariationProduct } from '../utils/variationUtils'
import { PriceHistoryModal } from '../../PriceHistory/PriceHistoryModal'
import styles from '../ProductForm.module.scss'
import { SelectProduct } from './SelectProduct'
import { VariationPrice } from './VariationPrice'
import { VariationStatus } from './VariationStatus'

const KEEPS_STATUS_ON_UNLINK = [
  EProductStatus.MODERATION,
  EProductStatus.REJECTED,
  EProductStatus.BLOCKED
]

interface IVariationsTableProps {
  form: FormInstance
  variations: IFrontVariation[]
  // вариативные характеристики, выбранные в заголовке таблицы
  axis1?: string
  axis2?: string
  onAxisChange: (position: 'first' | 'second', name: string | undefined) => void
  subCategoryCharacteristics: ISubCategoryCharacteristic[]
  // заполненные характеристики основного товара — только из них можно выбрать вариативную
  parentCharacteristics: ICharacteristic[]
  parentName?: string
  // индексы строк с ошибкой «значение без товара» — подсветка при сабмите
  rowErrors: Set<number>
  openUpdateProductModal: (variation: IFrontVariation, index: number) => void
  openCreateProductModal: (variation: IFrontVariation, index: number) => void
  openChooseProductModal: (variation: IFrontVariation, index: number) => void
  addToDeletedVariations: (productId: string) => void
}

export const VariationsTable: React.FC<IVariationsTableProps> = ({
  form,
  variations,
  axis1,
  axis2,
  onAxisChange,
  subCategoryCharacteristics,
  parentCharacteristics,
  parentName,
  rowErrors,
  openUpdateProductModal,
  openCreateProductModal,
  openChooseProductModal,
  addToDeletedVariations
}) => {
  const parentId: string | undefined = Form.useWatch('id', {
    form,
    preserve: true
  })
  const parentStatus: EProductStatus | undefined = Form.useWatch('status', {
    form,
    preserve: true
  })
  const parentPrice: string | number | undefined = Form.useWatch('price', {
    form,
    preserve: true
  })
  const [historyProductId, setHistoryProductId] = React.useState<string | null>(
    null
  )

  // Статус основного товара бэкенд каскадом переносит на вариации — перечитываем их
  const prevParentStatus = React.useRef(parentStatus)
  React.useEffect(() => {
    const prevStatus = prevParentStatus.current
    prevParentStatus.current = parentStatus
    if (!prevStatus || !parentStatus || prevStatus === parentStatus || !parentId) {
      return
    }
    productApi
      .getProduct(parentId)
      .then(({ data }) => {
        const freshById = new Map(
          (data.variations ?? []).map((item) => [item._id, item])
        )
        const current: IFrontVariation[] = form.getFieldValue('variations') ?? []
        form.setFieldValue(
          'variations',
          current.map((row) => {
            const fresh = row.product && freshById.get(row.product.id)
            return fresh && row.product
              ? { ...row, product: toVariationProduct(fresh, row.product.name) }
              : row
          })
        )
      })
      .catch(showServerError)
  }, [parentStatus])

  // Опции характеристик для заголовков — только заполненные у основного товара:
  // основной товар сам является первой вариацией со своими значениями.
  // Текущее значение оси добавляем даже если его нет в списке (старые/импортированные товары)
  const axisOptions = React.useMemo(() => {
    const names = new Set<string>()
    parentCharacteristics.forEach((c) => names.add(capitalizeFirstLetter(c.name.trim())))
    if (axis1) names.add(axis1)
    if (axis2) names.add(axis2)
    return Array.from(names)
  }, [parentCharacteristics, axis1, axis2])

  // значение характеристики основного товара по имени (для его строки в таблице)
  const getParentValue = (axisName?: string): string =>
    axisName
      ? parentCharacteristics.find(
        (c) => c.name.trim().toLowerCase() === axisName.toLowerCase()
      )?.value ?? ''
      : ''

  const optionsFor = (excludeName?: string) =>
    axisOptions
      .filter((name) => !excludeName || name.toLowerCase() !== excludeName.toLowerCase())
      .map((name) => ({ value: name, label: name }))

  const hasFilledRows = variations.some(
    (v) => v.product || v.firstCharacteristicValue?.trim() || v.secondCharacteristicValue?.trim()
  )

  const handleValueChange = (index: number, position: 'first' | 'second', value: string) => {
    const next = [...variations]
    next[index] = {
      ...next[index],
      ...(position === 'first'
        ? { firstCharacteristicValue: value }
        : { secondCharacteristicValue: value })
    }
    form.setFieldValue('variations', next)
  }

  const handleAddRow = () => {
    if (!axis1) return
    form.setFieldValue('variations', [
      ...variations,
      {
        firstCharacteristicName: axis1,
        firstCharacteristicValue: '',
        ...(axis2 && { secondCharacteristicName: axis2, secondCharacteristicValue: '' })
      }
    ])
  }

  const handleDeleteRow = (index: number) => {
    const row = variations[index]
    const productId = row?.product?.id
    const status = row?.product?.status
    if (!productId) {
      form.setFieldValue('variations', variations.filter((_, i) => i !== index))
      return
    }
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Открепить вариацию?',
      // Решения площадки открепление не снимает — статус остаётся прежним
      content:
        status && KEEPS_STATUS_ON_UNLINK.includes(status)
          ? `Вариация будет откреплена и станет отдельным товаром. Статус «${productTags[status].label}» сохранится`
          : 'Вариация будет откреплена и станет отдельным товаром со статусом "черновик". Потребуется опубликовать её вручную',
      icon: <ExclamationCircleOutlined />,
      okText: 'Открепить',
      cancelText: 'Закрыть',
      onOk() {
        bodyScrollLock.disable()
        // открепляет бэкенд при сохранении формы — товар попадает в toDeleteVariations
        addToDeletedVariations(productId)
        // строки могли поменяться, пока открыта модалка, — убираем по товару
        const current: IFrontVariation[] = form.getFieldValue('variations') ?? []
        form.setFieldValue(
          'variations',
          current.filter((item) => item.product?.id !== productId)
        )
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  // Статус меняется запросом: за время ответа строки могли поменяться, ищем по товару
  const handleProductChange = (product: IFrontVariationProduct) => {
    const current: IFrontVariation[] = form.getFieldValue('variations') ?? []
    form.setFieldValue(
      'variations',
      current.map((row) =>
        row.product?.id === product.id ? { ...row, product } : row
      )
    )
  }

  const renderValueCell = (row: IFrontVariation, index: number, position: 'first' | 'second') => {
    const axisName = position === 'first' ? axis1 : axis2
    if (!axisName) {
      return <Input size='middle' disabled placeholder='—' />
    }
    const matchingChar = subCategoryCharacteristics.find(
      (c) => c.name.toLowerCase() === axisName.toLowerCase()
    )
    const value =
      position === 'first' ? row.firstCharacteristicValue : row.secondCharacteristicValue

    if (matchingChar && !matchingChar.isAnyValue) {
      return (
        <Select
          size='middle'
          style={{ width: '100%' }}
          placeholder={`Выберите ${axisName.toLowerCase()}`}
          options={matchingChar.hints.map((hint) => ({ value: hint, label: hint }))}
          value={value || undefined}
          onChange={(val) => handleValueChange(index, position, val)}
          showSearch={false}
        />
      )
    }
    return (
      <Input
        size='middle'
        placeholder='Значение'
        value={value}
        onChange={(e) => handleValueChange(index, position, e.target.value)}
      />
    )
  }

  const cellStyle: React.CSSProperties = { display: 'flex', alignItems: 'center' }

  return (
    <div>
      <div className={styles.variationsTable}>
        {/* Заголовок таблицы: вариативные характеристики выбираются в шапке колонок */}
        <div className={styles.variationsHeader}>
          <Typography.Text strong className={styles.variationsHeaderLabel}>
            Товар
          </Typography.Text>
          <Select
            size='middle'
            placeholder='Характеристика 1'
            popupMatchSelectWidth={300}
            options={optionsFor(axis2)}
            notFoundContent={
              <Typography.Text type='secondary'>
                Сначала заполните хотя бы одну характеристику основного товара.
                Вариации создаются на основании заполненных характеристик
                основного товара
              </Typography.Text>
            }
            value={axis1 || undefined}
            onChange={(val) => onAxisChange('first', val)}
            // смена первой характеристики при заполненных строках сбрасывает несовместимые значения
            allowClear={!hasFilledRows}
            onClear={() => onAxisChange('first', undefined)}
          />
          <Select
            size='middle'
            placeholder='Характеристика 2'
            popupMatchSelectWidth={300}
            options={optionsFor(axis1)}
            notFoundContent={
              <Typography.Text type='secondary'>
                Заполните вторую характеристику основного товара. Вариации
                создаются на основании заполненных характеристик основного товара
              </Typography.Text>
            }
            value={axis2 || undefined}
            disabled={!axis1}
            onChange={(val) => onAxisChange('second', val)}
            allowClear
            onClear={() => onAxisChange('second', undefined)}
          />
          <Typography.Text strong className={styles.variationsHeaderLabel}>
            Стоимость
          </Typography.Text>
          <Typography.Text strong className={styles.variationsHeaderLabel}>
            Статус
          </Typography.Text>
          <div className={styles.variationsHeaderLabel} />
        </div>

        {/* Основной товар — первая строка таблицы, только для наглядности:
            редактируется в самой форме, значения берутся из общих характеристик
            и следуют за выбором характеристик в заголовке */}
        <div className={styles.variationsRow}>
          <div style={cellStyle}>
            <Tooltip title='Основной товар'>
              <Typography.Text style={{ flex: 1, minWidth: 0 }} ellipsis>
                {parentName || 'Основной товар'}
                <Typography.Text type='secondary'> — основной товар</Typography.Text>
              </Typography.Text>
            </Tooltip>
          </div>
          <div className={styles.variationsValues}>
            <div style={cellStyle}>
              <Input size='middle' disabled value={getParentValue(axis1)} placeholder='—' />
            </div>
            <div style={cellStyle}>
              <Input size='middle' disabled value={getParentValue(axis2)} placeholder='—' />
            </div>
            <div style={cellStyle}>
              <VariationPrice
                readOnly
                product={
                  parentId
                    ? {
                      id: parentId,
                      name: parentName ?? '',
                      price:
                        parentPrice !== undefined && parentPrice !== ''
                          ? Number(String(parentPrice).replace(',', '.'))
                          : undefined
                    }
                    : undefined
                }
                onOpenHistory={setHistoryProductId}
              />
            </div>
          </div>
          <div style={cellStyle}>
            {parentStatus ? (
              <Tag color={productTags[parentStatus].color}>
                {productTags[parentStatus].label}
              </Tag>
            ) : (
              <Typography.Text type='secondary'>—</Typography.Text>
            )}
          </div>
          <div />
        </div>

        {variations.map((row, index) => (
          <div key={index} className={styles.variationsRow}>
            <div style={cellStyle}>
              <SelectProduct
                variation={row}
                hasError={rowErrors.has(index)}
                openUpdateProductModal={() => openUpdateProductModal(row, index)}
                openCreateProductModal={() => openCreateProductModal(row, index)}
                openChooseProductModal={() => openChooseProductModal(row, index)}
              />
            </div>
            <div className={styles.variationsValues}>
              <div style={cellStyle}>{renderValueCell(row, index, 'first')}</div>
              <div style={cellStyle}>{renderValueCell(row, index, 'second')}</div>
              <div style={cellStyle}>
                <VariationPrice
                  product={row.product}
                  onChange={handleProductChange}
                  onOpenHistory={setHistoryProductId}
                />
              </div>
            </div>
            <div style={cellStyle}>
              <VariationStatus
                product={row.product}
                parentStatus={parentStatus}
                onChange={handleProductChange}
              />
            </div>
            <Button
              type='primary'
              htmlType='button'
              size='small'
              icon={<DeleteOutlined style={{ fontSize: 14 }} />}
              onClick={() => handleDeleteRow(index)}
              ghost
              danger
            />
          </div>
        ))}
      </div>

      <Button
        type='dashed'
        htmlType='button'
        icon={<PlusOutlined />}
        disabled={!axis1}
        onClick={handleAddRow}
        style={{ marginTop: 4 }}
      >
        Добавить вариацию
      </Button>
      {!axis1 && (
        <Typography.Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
          Выберите характеристику в заголовке таблицы, чтобы добавлять вариации
        </Typography.Paragraph>
      )}
      <PriceHistoryModal
        productId={historyProductId}
        onClose={() => setHistoryProductId(null)}
      />
    </div>
  )
}
